import { Flex } from '@chakra-ui/react';
import cloneDeep from 'lodash.clonedeep';
import { RootState, store, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectWidget, selectWidgetProperty, selectWidgets } from 'modules/Editor/store/infographSelector';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { ReactElement, useCallback, useMemo } from 'react';
import { LineWidgetData, LineWidgetTypes, Side } from 'widgets/Lines/LineWidget/LineWidget.types';
import { useMultiWidgetValue } from 'widgets/sdk/Toolbar/MultiWidgetToolbar/MultiWidgetValueProvider';

import { LineToolbarOptions, LineWidgetToolbarContent } from '../../common/toolbar/CommonLineToolbarContent';

/**
 * MULTI LINE WIDGET TOOLBAR
 *
 * Displayed when multiple widgets are selected and all are line widgets
 */
export const MultiLineWidgetToolbar = (): ReactElement => {
  const dispatch = useAppDispatch();
  const activeWidgets = useAppSelector(selectActiveWidgets);

  const firstActiveWidgetId = activeWidgets[0]?.id;
  const type = useAppSelector(
    selectWidgetProperty<LineWidgetData, 'type'>(firstActiveWidgetId, 'type'),
  ) as LineWidgetTypes;
  const { color: strokeColor } = useMultiWidgetValue();
  const strokeWidth = useAppSelector(
    selectWidgetProperty<LineWidgetData, 'strokeWidth'>(firstActiveWidgetId, 'strokeWidth'),
  );
  const strokeDashType = useAppSelector(
    selectWidgetProperty<LineWidgetData, 'strokeDashType'>(firstActiveWidgetId, 'strokeDashType'),
  );
  const startArrowStyle = useAppSelector(
    selectWidgetProperty<LineWidgetData, 'startArrowStyle'>(firstActiveWidgetId, 'startArrowStyle'),
  );
  const endArrowStyle = useAppSelector(
    selectWidgetProperty<LineWidgetData, 'endArrowStyle'>(firstActiveWidgetId, 'endArrowStyle'),
  );

  const updateWidgets = useCallback(
    (updateData: Partial<LineWidgetData>) => {
      const batchUpdateList = activeWidgets
        .map((widget) => {
          const storeState = store.getState() as RootState;
          const widgetData = selectWidget(widget.id)(storeState) as LineWidgetData;
          const newData = cloneDeep(updateData);

          // Prevent updating line type and arrow styles if the line is connected to center port
          const { startPort, endPort } = widgetData;
          if (startPort?.side === Side.CENTER || endPort?.side === Side.CENTER) {
            const updateKeys = Object.keys(updateData);
            if (updateKeys.includes('type')) {
              delete newData['type'];
            }

            if (startPort?.side === Side.CENTER && updateKeys.includes('startArrowStyle')) {
              delete newData['startArrowStyle'];
            }

            if (endPort?.side === Side.CENTER && updateKeys.includes('endArrowStyle')) {
              delete newData['endArrowStyle'];
            }
          }

          return {
            widgetId: widget.id,
            widgetData: newData,
          };
        })
        .filter(({ widgetData }) => Object.keys(widgetData).length > 0);

      dispatch(updateWidget(batchUpdateList));
    },
    [activeWidgets, dispatch],
  );

  const disabledOptions = useMemo(() => {
    let options: LineToolbarOptions[] = [];
    const storeState = store.getState() as RootState;
    const widgets = selectWidgets(storeState);

    const isAllStartCenter = activeWidgets.every(({ id }) => {
      const widget = widgets[id] as LineWidgetData;
      return widget.startPort?.side === Side.CENTER;
    });

    const isAllEndCenter = activeWidgets.every(({ id }) => {
      const widget = widgets[id] as LineWidgetData;
      return widget.endPort?.side === Side.CENTER;
    });

    if (isAllStartCenter || isAllEndCenter) {
      options.push(LineToolbarOptions.lineType);

      if (isAllStartCenter) {
        options.push(LineToolbarOptions.startArrow);
      }
      if (isAllEndCenter) {
        options.push(LineToolbarOptions.endArrow);
      }
    }

    return options;
  }, [activeWidgets]);

  return (
    <Flex mr='8px' gap='8px' align='center'>
      <LineWidgetToolbarContent
        values={{
          strokeColor: strokeColor! as string[],
          strokeWidth: strokeWidth!,
          strokeDashType: strokeDashType!,
          startArrowStyle: startArrowStyle!,
          endArrowStyle: endArrowStyle!,
          type: type!,
        }}
        onUpdate={updateWidgets}
        disabledOptions={disabledOptions}
      />
    </Flex>
  );
};
